import { ReactElement, useEffect, useRef } from 'react';
import styles from './chatView.module.scss';
import { AiArtifact, AuthorType, Message, ResponseType } from '../../../../types/message';
import UserMessage from '../message/UserMessage';
import AssistantMessage from '../message/AssistantMessage';
import MessageInput from '../messageInput/MessageInput';
import ChatSpinner from '../chatSpinner/ChatSpinner';
import TypingIndicator from '../typingIndicator/TypingIndicator';
import { PromptSuggestions } from '../../../../types/promptSuggestions';
import { ToolCall } from '@/modules/chat/types/chatResponse';
import ToolApproval from '../toolApproval/ToolApproval';
import PrivacyPopup from '../privacyPopup/PrivacyPopup';

type Props = {
  artifacts: AiArtifact;
  promptSuggestions?: PromptSuggestions;
  onSend: (message: Message) => void;
  messages: Message[];
  loading: boolean;
  onArtifactClick?: (artifactId: string) => void;
  pendingToolApprovals?: Array<ToolCall>;
  reviewApprovals?: (approvals: { [toolCallId: string]: boolean }) => void;
  primaryColor?: string;
  secondaryColor?: string;
  aiMessageIcon?: string;
  loadingLottieUrl?: string;
  lastScrollTop?: React.MutableRefObject<number>;
  showPrivacyPopup?: boolean;
  setShowPrivacyPopup?: (show: boolean) => void;
};

const ChatView = ({
  artifacts,
  promptSuggestions,
  onSend,
  messages,
  loading,
  onArtifactClick,
  pendingToolApprovals,
  reviewApprovals,
  primaryColor,
  secondaryColor,
  aiMessageIcon,
  loadingLottieUrl,
  lastScrollTop,
  showPrivacyPopup,
  setShowPrivacyPopup,
}: Props): ReactElement => {
  const listRef = useRef<HTMLDivElement>(null);

  const containerStyle = {
    ...(primaryColor ? { '--primary-color': primaryColor } : {}),
    ...(secondaryColor ? { '--secondary-color': secondaryColor } : {}),
  } as React.CSSProperties;

  useEffect(() => {
    if (listRef.current && lastScrollTop && lastScrollTop.current > 0) {
      listRef.current.scrollTop = lastScrollTop.current;
    }
  }, []);

  useEffect(() => {
    const handleViewportResize = (): void => {
      // Small delay to let the layout finish
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      }, 100);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
    };
  }, []);

  useEffect(() => {
    if (!listRef.current || (lastScrollTop && lastScrollTop.current > 0 && messages.length > 0 && !loading)) {
      // If we just restored scroll, don't auto-scroll to bottom unless there's a new message
      // Actually, simple scroll restoration is enough. Let's refine.
    }
    // Existing auto-scroll logic
    const element = listRef.current;
    if (!element) return;

    // Only auto-scroll to bottom if we are near the bottom or it's a new message from user/ai
    const from = element.scrollTop;
    const to = element.scrollHeight;
    if (from === to) {
      return;
    }

    const DURATION_MILLIS = 500;
    const start = performance.now();

    const easeOutQuad = (timePercent: number): number => 1 - (1 - timePercent) * (1 - timePercent);

    const frame = (): void => {
      const timePercent = (performance.now() - start) / DURATION_MILLIS;
      const progress = easeOutQuad(Math.min(timePercent, 1));

      element.scrollTop = from + (to - from) * progress;

      if (timePercent < 1) {
        requestAnimationFrame(frame);
      }
    };

    requestAnimationFrame(frame);
  }, [loading, pendingToolApprovals]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    if (lastScrollTop) {
      lastScrollTop.current = (e.target as HTMLDivElement).scrollTop;
    }
  };

  const handleOptionClick = (option: string, responseType: ResponseType): void => {
    onSend({
      authorType: AuthorType.USER,
      content: option,
      responseType,
    });
  };

  return (
    <div className={styles['chat-view']} style={containerStyle}>
      <div ref={listRef} className={styles['message-list']} onScroll={handleScroll}>
        {promptSuggestions && (
          <AssistantMessage
            artifacts={artifacts}
            message={{
              authorType: AuthorType.AI,
              content: 'Sugerencias',
              options: promptSuggestions,
            }}
            onOptionClick={handleOptionClick}
            onArtifactClick={onArtifactClick}
            isLatestMessage={messages.length === 0}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            aiMessageIcon={aiMessageIcon}
          />
        )}
        {messages.map((message, index) => {
          if (message.responseType === 'option') {
            return null;
          }

          return message.authorType === AuthorType.AI ? (
            <AssistantMessage
              key={index}
              artifacts={artifacts}
              message={message}
              onOptionClick={handleOptionClick}
              onArtifactClick={onArtifactClick}
              isLatestMessage={
                index === messages.length - 1 && message.authorType === AuthorType.AI
              }
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              aiMessageIcon={aiMessageIcon}
            />
          ) : (
            <UserMessage
              key={index}
              message={message}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            />
          );
        })}

        {pendingToolApprovals && pendingToolApprovals.length > 0 && reviewApprovals && (
          <ToolApproval approvals={pendingToolApprovals} onReview={reviewApprovals} />
        )}

        {loading && <TypingIndicator />}
      </div>
      {showPrivacyPopup && setShowPrivacyPopup && (
        <PrivacyPopup
          onClose={() => setShowPrivacyPopup(false)}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      )}
      <MessageInput
        enabled={!loading && (!pendingToolApprovals || pendingToolApprovals.length === 0)}
        onSend={message =>
          onSend({
            authorType: AuthorType.USER,
            content: message,
          })
        }
      />
    </div>
  );
};

export default ChatView;
